import { useState } from 'react';
import { submitLead } from '../api';
import { Phone, Mail, MapPin, Clock, Check } from './Icons';

const EMPTY = { name: '', email: '', phone: '', subject: '', message: '', website: '' };

export default function Contact({ company }) {
  const c = company.contact || {};
  const [form, setForm] = useState(EMPTY);
  const [status, setStatus] = useState('idle'); // idle | sending | success | error
  const [error, setError] = useState('');

  const update = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === 'sending') return;
    setStatus('sending');
    setError('');

    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      message: form.message.trim(),
      website: form.website, // honeypot — must stay empty
    };
    if (form.phone.trim()) payload.phone = form.phone.trim();
    if (form.subject.trim()) payload.subject = form.subject.trim();

    const result = await submitLead(payload);
    if (result.ok) {
      setStatus('success');
      setForm(EMPTY);
    } else {
      setStatus('error');
      setError(result.error);
    }
  };

  return (
    <section className="section section--contact" id="contact">
      <div className="container contact__grid">
        <div className="contact__info">
          <span className="eyebrow">Get in touch</span>
          <h2 className="section__title">Let&rsquo;s secure and connect your business</h2>
          <p className="contact__lead">
            Tell us what you need — a CCTV or Wi-Fi installation, networking, or early access to our
            digital tools. We&rsquo;ll get back to you with a tailored quote.
          </p>

          <ul className="contact__list">
            {c.phone && (
              <li>
                <span className="contact__ico"><Phone width={18} height={18} /></span>
                <a href={`tel:${c.phone.replace(/\s+/g, '')}`}>{c.phone}</a>
              </li>
            )}
            {c.email && (
              <li>
                <span className="contact__ico"><Mail width={18} height={18} /></span>
                <a href={`mailto:${c.email}`}>{c.email}</a>
              </li>
            )}
            {c.country && (
              <li>
                <span className="contact__ico"><MapPin width={18} height={18} /></span>
                {c.country}
              </li>
            )}
            {company.hours && (
              <li>
                <span className="contact__ico"><Clock width={18} height={18} /></span>
                {company.hours}
              </li>
            )}
          </ul>
        </div>

        <div className="contact__form-wrap card">
          {status === 'success' ? (
            <div className="form-success" role="status">
              <div className="form-success__icon">
                <Check width={30} height={30} />
              </div>
              <h3>Message sent!</h3>
              <p>Thanks for reaching out. We&rsquo;ll be in touch shortly.</p>
              <button className="btn btn--ghost btn--sm" onClick={() => setStatus('idle')}>
                Send another
              </button>
            </div>
          ) : (
            <form className="form" onSubmit={onSubmit} noValidate>
              <div className="form__row">
                <label className="field">
                  <span>Name</span>
                  <input
                    name="name"
                    value={form.name}
                    onChange={update}
                    required
                    minLength={2}
                    maxLength={120}
                    placeholder="Your name"
                    autoComplete="name"
                  />
                </label>
                <label className="field">
                  <span>Email</span>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={update}
                    required
                    placeholder="you@company.com"
                    autoComplete="email"
                  />
                </label>
              </div>

              <div className="form__row">
                <label className="field">
                  <span>Phone <em>(optional)</em></span>
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={update}
                    maxLength={40}
                    placeholder="+254 7xx xxx xxx"
                    autoComplete="tel"
                  />
                </label>
                <label className="field">
                  <span>Subject <em>(optional)</em></span>
                  <input
                    name="subject"
                    value={form.subject}
                    onChange={update}
                    maxLength={200}
                    placeholder="e.g. CCTV for my office"
                  />
                </label>
              </div>

              <label className="field">
                <span>Message</span>
                <textarea
                  name="message"
                  value={form.message}
                  onChange={update}
                  required
                  minLength={5}
                  maxLength={5000}
                  rows={5}
                  placeholder="Tell us about your project…"
                />
              </label>

              {/* Honeypot: hidden from users, catches bots. */}
              <input
                className="hp"
                type="text"
                name="website"
                value={form.website}
                onChange={update}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
              />

              {status === 'error' && (
                <p className="form__error" role="alert">
                  {error}
                </p>
              )}

              <button className="btn btn--primary btn--block" type="submit" disabled={status === 'sending'}>
                {status === 'sending' ? 'Sending…' : 'Send message'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
